import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { BrandWordmark } from '../components/BrandWordmark';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // When the global 401 interceptor bounces a user here, we receive
  // `?reason=session_expired&next=/admin/...` — show a banner and preserve
  // the deep-link so we can return them to it after a successful login.
  const searchParams = new URLSearchParams(location.search);
  const sessionExpired = searchParams.get('reason') === 'session_expired';
  const next = searchParams.get('next');

  // Force dark theme on the login/splash regardless of the user's saved
  // preference. When light-theme was active it was rewriting `text-white/85`
  // on the feature bullets to dark text-on-dark, washing them out entirely.
  useEffect(() => {
    const html = document.documentElement;
    const hadLight = html.classList.contains('light-theme');
    if (hadLight) html.classList.remove('light-theme');
    return () => {
      if (hadLight) html.classList.add('light-theme');
    };
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      // If we have a deep-link target, prefer it; otherwise route by role.
      if (next) {
        navigate(next);
      } else if (user?.role === 'master_admin') {
        navigate('/select-mode');
      } else if (user?.domain_id) {
        navigate('/my-domain');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0D1117] via-[#0E1F2E] to-[#0A0F1C] flex-col justify-between p-12 border-r border-white/5">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png"
            alt="Yabbit"
            className="h-12 w-auto object-contain"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </div>
        <div className="space-y-8">
          <div>
            <BrandWordmark variant="hero-stacked" testId="login-brand-heading" />
            <p className="text-white/70 mt-4 text-lg">
              AI-powered call grading, coaching and revenue intelligence for your team.
            </p>
          </div>
          <div className="space-y-4">
            {[
              'Real-time call transcription & AI scoring',
              'Multi-vertical knowledge base grading',
              'Automated coaching recommendations',
              'Revenue correlation & team dashboards',
            ].map(f => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-teal-500/25 flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-teal-400" />
                </div>
                <span className="text-white/85 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/45 text-xs">Powered by Yabbit UCaaS · UPTI</p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <img
              src="https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png"
              alt="Yabbit"
              className="h-10 w-auto object-contain mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <BrandWordmark
              variant="hero-inline"
              testId="login-brand-heading-mobile"
              className="mt-4"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white">Sign in</h2>
            <p className="text-white/40 text-sm mt-1">Enter your credentials to access the platform</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Email address</label>
              <input
                type="text"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-teal-500/60 transition-colors"
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-white/50">Password</label>
                <Link to="/forgot-password" className="text-xs text-teal-400/80 hover:text-teal-300 font-medium"
                  data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-teal-500/60 transition-colors pr-11"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {sessionExpired && !error && (
              <div
                className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-amber-300 text-sm flex items-start gap-2"
                data-testid="session-expired-banner"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Your session expired. Please sign in again to continue.</span>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 disabled:from-white/10 disabled:to-white/10 disabled:text-white/30 text-white font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/5 text-center">
            <p className="text-white/30 text-sm mb-3">Explore with sample data?</p>
            <Link
              to="/demo"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/10 hover:border-teal-500/40 bg-white/5 hover:bg-teal-500/5 text-white/60 hover:text-white text-sm font-medium rounded-xl transition-all"
              data-testid="demo-link"
            >
              Try Demo Mode →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
